import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Spinner } from '@cockpit/ui'
import { AppShell } from '@/components/layout/AppShell'
import { PublicOnly, RequireAuth, RequireSuperAdmin } from './guards'

const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const OnboardingPage = lazy(() => import('@/features/auth/pages/OnboardingPage'))
const DeskPage = lazy(() => import('@/features/desk/pages/DeskPage'))
const ArticlesPage = lazy(() => import('@/features/articles/pages/ArticlesPage'))
const ArticleDetailPage = lazy(() => import('@/features/article-detail/pages/ArticleDetailPage'))
const GenerationPage = lazy(() => import('@/features/generation/pages/GenerationPage'))
const TopicListPage = lazy(() => import('@/features/topics/pages/TopicListPage'))
const TopicDetailPage = lazy(() => import('@/features/topics/pages/TopicDetailPage'))
const DeliveriesPage = lazy(() => import('@/features/deliveries/pages/DeliveriesPage'))
const SettingsPage = lazy(() => import('@/features/tenants/pages/SettingsPage'))
const ResearchRunsPage = lazy(() => import('@/features/research/pages/ResearchRunsPage'))
const ResearchRunDetailPage = lazy(() => import('@/features/research/pages/ResearchRunDetailPage'))
const OpsPage = lazy(() => import('@/features/ops/pages/OpsPage'))
const AuditPage = lazy(() => import('@/features/ops/pages/AuditPage'))
const TenantListPage = lazy(() => import('@/features/tenants/pages/TenantListPage'))
const TenantDetailPage = lazy(() => import('@/features/tenants/pages/TenantDetailPage'))
const LocaleAuditPage = lazy(() => import('@/features/locale-audit/pages/LocaleAuditPage'))
const GeoMentionsPage = lazy(() => import('@/features/seo/pages/GeoMentionsPage'))
const SeoSpendPage = lazy(() => import('@/features/seo/pages/SeoSpendPage'))
const SeoPlatformPage = lazy(() => import('@/features/seo/pages/SeoPlatformPage'))

function PageFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner />
    </div>
  )
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnly>
              <LoginPage />
            </PublicOnly>
          }
        />
        <Route path="/onboarding" element={<OnboardingPage />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/desk" replace />} />
          <Route path="/desk" element={<DeskPage />} />
          <Route path="/articles" element={<ArticlesPage />} />
          <Route path="/articles/:id" element={<ArticleDetailPage />} />
          <Route path="/articles/:id/generation" element={<GenerationPage />} />
          <Route path="/topics" element={<TopicListPage />} />
          <Route path="/topics/:id" element={<TopicDetailPage />} />
          <Route path="/deliveries" element={<DeliveriesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/research" element={<ResearchRunsPage />} />
          <Route path="/research/:id" element={<ResearchRunDetailPage />} />
          <Route path="/seo/geo" element={<GeoMentionsPage />} />
          <Route path="/seo/spend" element={<SeoSpendPage />} />
          {/* Path-scoped aliases: tenant id in URL so middle-click / deep-links
              cannot pick the wrong switcher scope. Same page components. */}
          <Route path="/tenants/:id/seo/geo" element={<GeoMentionsPage />} />
          <Route path="/tenants/:id/seo/spend" element={<SeoSpendPage />} />
          <Route
            path="/seo/platform"
            element={
              <RequireSuperAdmin>
                <SeoPlatformPage />
              </RequireSuperAdmin>
            }
          />
          <Route path="/ops" element={<OpsPage />} />
          <Route
            path="/audit"
            element={
              <RequireSuperAdmin>
                <AuditPage />
              </RequireSuperAdmin>
            }
          />
          <Route
            path="/tenants"
            element={
              <RequireSuperAdmin>
                <TenantListPage />
              </RequireSuperAdmin>
            }
          />
          <Route
            path="/tenants/:id"
            element={
              <RequireSuperAdmin>
                <TenantDetailPage />
              </RequireSuperAdmin>
            }
          />
          <Route
            path="/locale-audit"
            element={
              <RequireSuperAdmin>
                <LocaleAuditPage />
              </RequireSuperAdmin>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/desk" replace />} />
      </Routes>
    </Suspense>
  )
}
